/**
 * /getting-started — beginner's path for homeowners new to TurfPulse.
 *
 * Tone: warm, calm, never urgent. No gating, no upsells in the body. The
 * Inner Circle CTA appears once at the bottom — and only after we've earned
 * the visitor's trust by walking them through the free flow first.
 *
 * Audience: someone who just clicked into TurfPulse from a Facebook post
 * or a friend's recommendation, has 5 minutes, and isn't sure what GDD
 * even means. They want to know "what is this thing, and what do I have
 * to do to use it."
 *
 * Structure:
 *   1. Hero — "New here? Start with the basics."
 *   2. The journey (5 steps, time-estimated, optional steps marked clearly)
 *   3. "Things you DON'T need to start" — disarm the hardware fear
 *   4. Glossary — GDD, ET₀, SVWC, Smith-Kerns, FAO-56 in plain English
 *   5. FAQ — the 6 questions a beginner actually asks
 *   6. Final soft CTA
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Sprout, ArrowRight, Clock, CheckCircle, BookOpen, HelpCircle,
  Sparkles, ShieldCheck, Heart,
} from 'lucide-react';

const Step = ({ number, title, time, optional = false, children, testid }) => (
  <div
    className="relative pl-12 pb-10 last:pb-0 border-l-2 border-dashed border-emerald-200 dark:border-emerald-800 last:border-l-transparent"
    data-testid={testid}
  >
    <div className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-emerald-600 dark:bg-emerald-500 text-white flex items-center justify-center font-black text-sm shadow-md">
      {number}
    </div>
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">{title}</h3>
      <div className="inline-flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
        <Clock className="w-3 h-3" /> {time}
      </div>
      {optional && (
        <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800 dark:text-amber-200">
          Optional
        </span>
      )}
    </div>
    <div className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const TermDef = ({ term, def, testid }) => (
  <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-4" data-testid={testid}>
    <dt className="font-mono text-sm font-bold text-emerald-700 dark:text-emerald-400">{term}</dt>
    <dd className="mt-1 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">{def}</dd>
  </div>
);

export default function GettingStartedPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 to-white dark:from-zinc-950 dark:to-zinc-900"
      data-testid="getting-started-page"
    >
      <SEO
        title="Getting Started — TurfPulse"
        description="New to TurfPulse? A calm, beginner-friendly walkthrough of the free GDD brain, optional hardware path, and the math behind every recommendation. Built for homeowners starting their lawn journey."
        canonical="https://getturfpulse.com/getting-started"
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Getting Started', path: '/getting-started' },
        ]}
      />
      <FaqJsonLd
        items={[
          {
            q: 'I have never heard of GDD. Do I need to understand it to use TurfPulse?',
            a: "No. We translate GDD into plain English on every screen — 'pre-emergent window opens in 8 days,' 'PGR re-application now,' 'water-budget says skip Tuesday.' GDD is the math under the hood. You read the recommendation. We show our work in the methodology page if you ever want to look under the hood.",
          },
          {
            q: 'Do I need any hardware to start using TurfPulse?',
            a: "No. The free tier works on weather data alone. Soil probes (Ecowitt) and a smart controller (Rachio) unlock the closed-loop probe-driven irrigation engine, but you can run TurfPulse for years on the free tier without spending a dollar on hardware.",
          },
          {
            q: 'What is the difference between Free, Homeowner, Enthusiast, and Inner Circle?',
            a: "Free covers GDD tracking, weather, and the dashboard — no card needed, ever. Homeowner Seasonal ($50/6mo) adds the PGR and Weed Control departments, equipment log, and email alerts. Enthusiast Annual ($80/yr) adds Pest, Disease, Soil Analysis, the 5-day spray forecast, and unlimited zones. Inner Circle ($4.99/mo locked for life, 10 seats) is for founding members who want the closed-loop probe-driven irrigation engine and direct line to the founder.",
          },
          {
            q: 'How long until I see results?',
            a: "Day one: you see the dashboard, weather, GDD-to-date, dewpoint, dollar-spot pressure, and pre-emergent window. Week one: you start logging applications and seeing the calendar fill in. Month one: you have a documented spray history, you know your local GDD curve, and you're treating only when the math says to. Year one: you have full-season comparisons across years, and you know exactly what your lawn responded to.",
          },
          {
            q: 'What if I am not in Round Rock, Texas?',
            a: "TurfPulse works anywhere with weather data — which is everywhere. The Texas-specific extras (rebate finder, Round Rock watering ordinance) are bonus content for our home turf. The core engine (GDD, ET₀, dollar-spot, dewpoint) is universal and pulls from your local NWS station.",
          },
          {
            q: 'I already use Rachio Weather Intelligence. Why do I need TurfPulse?',
            a: "Rachio WI skips a fire when it's about to rain — that's it. TurfPulse adds: a per-zone soil-probe override (water when the probe says, regardless of forecast), the TAMU 12% drought-floor override, a sensor-gated skip stack (rain + freeze + wind + dewpoint + MAD + city blackout), Smart-Split scheduling, application-aware auto-pause, and activity-feed receipts on every fire. Rachio is the controller. TurfPulse is the brain on top.",
          },
        ]}
      />

      {/* Hero */}
      <section className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="mx-auto max-w-3xl px-6 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
            <Sprout className="w-3.5 h-3.5" /> New to TurfPulse?
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            Start here. Take your time.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-700 dark:text-zinc-300 leading-relaxed">
            We built TurfPulse for homeowners who like their lawn but don't want
            it to take over their life. This page walks you through everything
            you need to know — calmly, in order, with a clear "you don't need
            this yet" line at every step.
          </p>
          <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
            ~5 minutes to read · You won't be asked for a credit card.
          </p>
        </div>
      </section>

      {/* The journey */}
      <section className="mx-auto max-w-3xl px-6 py-14" data-testid="getting-started-journey">
        <div className="flex items-center gap-2 mb-8">
          <CheckCircle className="w-7 h-7 text-emerald-700 dark:text-emerald-400" />
          <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
            Your first month with TurfPulse
          </h2>
        </div>
        <div className="space-y-0">
          <Step number="1" title="Make a free account" time="2 minutes" testid="step-1">
            <p>
              Email + password. We never sell your data, never email you marketing
              fluff, and you keep the free tier forever. The free tier is not a
              trial — there's no clock running, no card required.
            </p>
            <p>
              <Link to="/?auth=1" className="text-emerald-700 dark:text-emerald-400 font-semibold underline">
                Make your free account →
              </Link>
            </p>
          </Step>

          <Step number="2" title="Tell us about your lawn" time="3 minutes" testid="step-2">
            <p>
              Zip code (so we know your local weather), grass type (bermuda,
              St. Augustine, zoysia, etc.), and roughly how much lawn you have.
              That's it. No survey, no email funnel.
            </p>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 italic">
              You can change any of this later. We don't lock you in.
            </p>
          </Step>

          <Step number="3" title="Read the dashboard for a few days" time="5 minutes/day for a week" testid="step-3">
            <p>
              The dashboard shows you, in plain English, what your lawn is up
              against this week: temperature, soil temp estimate, GDD progress,
              dewpoint band, dollar-spot pressure, and the next pre-emergent or
              PGR window.
            </p>
            <p>
              Don't try to act on everything. Just <strong>read it</strong>. Within a
              week you'll start to recognize the pattern: <em>oh, dewpoint went
              up Wednesday and now disease pressure climbed</em>. That's the
              whole point.
            </p>
            <p className="text-sm">
              When you're ready to read more:{' '}
              <Link to="/methodology/probe-driven-irrigation" className="text-emerald-700 dark:text-emerald-400 underline font-semibold">
                see the methodology
              </Link>{' '}
              ·{' '}
              <Link to="/credits" className="text-emerald-700 dark:text-emerald-400 underline font-semibold">
                see our citations
              </Link>
            </p>
          </Step>

          <Step number="4" title="Add a smart controller" time="20 minutes" optional testid="step-4">
            <p>
              When you're comfortable with the dashboard and want TurfPulse to
              actually run your sprinklers — connect a Rachio. We never touch
              your password; you paste a Rachio API key from the Rachio app, and
              we use that to fire and pause zones.
            </p>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 italic">
              Don't have a Rachio? You can keep running TurfPulse for the
              advisory tiles forever. The controller-integration is optional.
            </p>
          </Step>

          <Step number="5" title="Add soil moisture probes" time="30 minutes" optional testid="step-5">
            <p>
              The endgame: bury 1–3 Ecowitt soil probes (one in your driest
              zone, one in your wettest, optional one in average) about 4″ deep
              in the active root zone. TurfPulse polls them every 15 min and
              uses the readings to fire only when the soil actually needs water
              — not when the forecast says it might.
            </p>
            <p className="text-sm">
              <strong>You do not need a weather station.</strong> Your local NWS feed
              already covers forecast, ET₀, and dewpoint inputs. The probes are
              the only Ecowitt sensors you actually need — and they're the
              piece your Rachio can't read on its own. We're the bridge.
            </p>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 italic">
              This is the <em>closed-loop probe-driven</em> setup our methodology
              page describes. Optional, but if you've ever watered a lawn that
              didn't need it (or skipped one that did), this is the fix.
            </p>
          </Step>
        </div>
      </section>

      {/* Things you don't need */}
      <section className="border-y border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950" data-testid="getting-started-not-needed">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="w-7 h-7 text-sky-700 dark:text-sky-400" />
            <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
              Things you don't need to start
            </h2>
          </div>
          <ul className="space-y-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
            <li className="flex items-start gap-3">
              <span className="text-sky-600 mt-1">✗</span>
              <span><strong>A credit card.</strong> Ever, on the free tier.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-sky-600 mt-1">✗</span>
              <span><strong>Smart hardware.</strong> Rachio + Ecowitt are great. They're also optional.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-sky-600 mt-1">✗</span>
              <span><strong>An agronomy degree.</strong> We translate the math into recommendations. The math is on the methodology page if you ever want it.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-sky-600 mt-1">✗</span>
              <span><strong>To be in Texas.</strong> The engine is universal. Texas-specific extras are bonus content.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-sky-600 mt-1">✗</span>
              <span><strong>To know what you're doing.</strong> That's literally why this exists.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Glossary */}
      <section className="mx-auto max-w-3xl px-6 py-14" data-testid="getting-started-glossary">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="w-7 h-7 text-emerald-700 dark:text-emerald-400" />
          <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
            Vocabulary, in plain English
          </h2>
        </div>
        <p className="mb-5 text-zinc-700 dark:text-zinc-300">
          You'll see these terms around the app. Here's what they actually mean.
        </p>
        <dl className="grid gap-3 sm:grid-cols-2">
          <TermDef
            testid="glossary-gdd"
            term="GDD"
            def="Growing Degree Days. A running total of how warm it's been. Drives pre-emergent and PGR timing because plants and pests respond to heat, not calendar dates."
          />
          <TermDef
            testid="glossary-et0"
            term="ET₀"
            def="Reference evapotranspiration. How many millimeters of water your lawn would lose to the atmosphere on a given day if it had unlimited water. The water-budget tile uses this."
          />
          <TermDef
            testid="glossary-svwc"
            term="SVWC"
            def="Soil Volumetric Water Content. The peer-reviewed scale for soil moisture (% of soil volume that's water). TAMU's wilt-stress threshold for bermuda is ~12% SVWC on fine sandy loam."
          />
          <TermDef
            testid="glossary-mad"
            term="MAD"
            def="Management Allowed Depletion. The fraction of plant-available water you'll let the soil lose before re-watering. We default to 0.50 for warm-season turf."
          />
          <TermDef
            testid="glossary-pgr"
            term="PGR"
            def="Plant Growth Regulator. A product that slows vertical growth so the lawn pushes laterally instead. Application timing is driven by GDD."
          />
          <TermDef
            testid="glossary-dewpoint"
            term="Dewpoint"
            def="The temperature air would need to cool to before water condenses. When dewpoints sit in the 60–70°F band overnight, leaves stay wet long enough for diseases like dollar spot to take off."
          />
          <TermDef
            testid="glossary-dollar-spot"
            term="Dollar Spot Pressure"
            def="A 0–100 score from the Smith-Kerns 2018 model (5-day moving avg humidity × daily mean temp). Above 20 = the weather is suitable for dollar spot. Doesn't mean it's there — means it could be."
          />
          <TermDef
            testid="glossary-fao56"
            term="FAO-56"
            def="The Food and Agriculture Organization's 1998 paper on crop evapotranspiration. The reference for daily water-use math worldwide. We use the Penman-Monteith equation from this paper."
          />
        </dl>
      </section>

      {/* FAQ */}
      <section className="border-y border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950" data-testid="getting-started-faq">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <div className="flex items-center gap-2 mb-6">
            <HelpCircle className="w-7 h-7 text-emerald-700 dark:text-emerald-400" />
            <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
              Honest answers to questions you're probably wondering
            </h2>
          </div>
          <div className="space-y-5">
            {[
              {
                q: 'I have never heard of GDD. Do I need to understand it to use TurfPulse?',
                a: "No. We translate GDD into plain English on every screen — 'pre-emergent window opens in 8 days,' 'PGR re-application now,' 'water-budget says skip Tuesday.' GDD is the math under the hood. You read the recommendation. We show our work in the methodology page if you ever want to look under the hood.",
              },
              {
                q: 'Do I need any hardware to start using TurfPulse?',
                a: "No. The free tier works on weather data alone. Soil probes (Ecowitt) and a smart controller (Rachio) unlock the closed-loop probe-driven irrigation engine, but you can run TurfPulse for years on the free tier without spending a dollar on hardware.",
              },
              {
                q: 'How long until I see results?',
                a: "Day one: you see the dashboard, weather, GDD, dewpoint, dollar-spot pressure, and pre-emergent window. Week one: you start logging applications and seeing the calendar fill in. Month one: you have a documented spray history and you're treating only when the math says to. Year one: full-season comparisons across years, and you know exactly what your lawn responded to.",
              },
              {
                q: 'What if I am not in Round Rock, Texas?',
                a: "TurfPulse works anywhere with weather data — which is everywhere. The Texas-specific extras (rebate finder, watering ordinance) are bonus content. The engine is universal and pulls from your local NWS station.",
              },
              {
                q: 'I already use Rachio Weather Intelligence. Why do I need TurfPulse?',
                a: "Rachio WI skips a fire when it's about to rain — that's it. TurfPulse adds: a per-zone soil-probe override, the TAMU 12% drought-floor override, a sensor-gated skip stack (rain + freeze + wind + dewpoint + MAD + city blackout), Smart-Split scheduling, application-aware auto-pause, and activity-feed receipts on every fire. Rachio is the controller. TurfPulse is the brain on top.",
              },
              {
                q: 'I want to chat with someone real before I sign up.',
                a: "Email hello@turfpulse.app. We answer every email personally — there's no support tier or ticketing system. We're a small team and we'd rather talk to you for ten minutes today than convert you cold next month.",
              },
            ].map((item, idx) => (
              <details
                key={idx}
                className="group rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40 p-4 sm:p-5 [&_summary::-webkit-details-marker]:hidden"
                data-testid={`faq-item-${idx}`}
              >
                <summary className="cursor-pointer flex items-start justify-between gap-4">
                  <span className="font-semibold text-zinc-900 dark:text-zinc-50">{item.q}</span>
                  <span className="shrink-0 text-emerald-700 dark:text-emerald-400 transition-transform group-open:rotate-45 text-2xl leading-none font-light">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final soft CTA */}
      <section className="mx-auto max-w-3xl px-6 py-16 text-center" data-testid="getting-started-final-cta">
        <Card className="border-emerald-300 dark:border-emerald-700 bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-emerald-950/40 dark:via-zinc-950 dark:to-amber-950/30">
          <CardContent className="p-8 sm:p-10">
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
              <h3 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
                Ready when you are.
              </h3>
            </div>
            <p className="mt-3 text-base text-zinc-700 dark:text-zinc-200 leading-relaxed max-w-xl mx-auto">
              The free account takes two minutes and never asks for a card.
              Start there. Read the dashboard for a week. Come back when you
              want more.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/?auth=1">
                <Button
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="getting-started-cta-signup"
                >
                  Make your free account <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/methodology/probe-driven-irrigation">
                <Button variant="outline" data-testid="getting-started-cta-methodology">
                  See the methodology first
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="ghost" data-testid="getting-started-cta-pricing">
                  Compare plans →
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-zinc-600 dark:text-zinc-400 italic flex items-center justify-center gap-1">
              <Heart className="w-3 h-3 text-rose-400" /> No upsells. No fluff. We promise.
            </p>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
